/**
 * ChatWidget - AI Study Assistant
 *
 * A floating chat widget that renders at the bottom-right of the screen.
 * Uses @chatscope/chat-ui-kit-react for the chat UI and communicates
 * with the LMS backend to proxy messages to OpenAI ChatGPT.
 *
 * The widget is lesson-aware: it passes the current lesson context to
 * the backend so the AI can provide relevant, lesson-specific help.
 */

import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";

import {
  ChatContainer,
  MainContainer,
  Message,
  MessageInput,
  MessageList,
  TypingIndicator
} from "@chatscope/chat-ui-kit-react";
import {
  Box,
  Flex,
  Heading,
  IconButton,
  Portal,
  Spinner,
  Text
} from "@chakra-ui/react";
import { NavigationIcons } from "@euka-mfes/bark";
import { useUserSelector } from "@context/hooks";
import { useChat } from "@hooks/useChat";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  chatBodyStyles,
  chatHeaderStyles,
  chatToggleButtonStyles,
  chatWindowStyles,
  chatscopeCssOverrides
} from "./chatStyles";

interface ChatWidgetProps {
  /** Lesson key string identifying the current lesson */
  lessonKey: string;
}

const ChatWidget = ({ lessonKey }: ChatWidgetProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { headerDetails } = useUserSelector();
  const firstName = headerDetails?.student?.first_name || "there";
  const styleRef = useRef<HTMLStyleElement | null>(null);

  const { messages, isLoading, isSending, error, sendMessage, clearHistory } =
    useChat({ lessonKey });

  // Inject chatscope CSS overrides once
  useEffect(() => {
    if (!styleRef.current) {
      const style = document.createElement("style");
      style.textContent = chatscopeCssOverrides;
      document.head.appendChild(style);
      styleRef.current = style;
    }
    return () => {
      if (styleRef.current) {
        styleRef.current.remove();
        styleRef.current = null;
      }
    };
  }, []);

  const handleSend = useCallback(
    (text: string) => {
      // chatscope passes HTML; use DOMParser to safely extract plain text
      const doc = new DOMParser().parseFromString(text, "text/html");
      const plainText = (doc.body.textContent ?? "").trim();
      if (plainText) {
        sendMessage(plainText);
      }
    },
    [sendMessage]
  );

  const handleToggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleClear = useCallback(async () => {
    await clearHistory();
  }, [clearHistory]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  return (
    <>
      {/* Floating toggle button */}
      <Box
        className="print-hide"
        css={chatToggleButtonStyles}
        onClick={handleToggle}
        aria-label={isOpen ? "Close study assistant" : "Open study assistant"}
        data-testid="chat-toggle-button"
      >
        {isOpen ? <NavigationIcons.Cross size="lg" /> : <ChatIcon />}
      </Box>

      {/* Chat window */}
      {isOpen && (
        <Portal>
          <Box
            className="print-hide"
            css={chatWindowStyles}
            data-testid="chat-window"
          >
            {/* Header */}
            <Box css={chatHeaderStyles}>
              <Flex direction="column">
                <Heading as="h4" textStyle="body.large" fontWeight="semibold">
                  Study Assistant
                </Heading>
                <Text fontSize="12px" color="base.200">
                  Ask me anything about your lesson
                </Text>
              </Flex>
              <Flex gap={1}>
                <IconButton
                  aria-label="Clear chat"
                  size="sm"
                  variant="ghost"
                  color="base.200"
                  _hover={{ bg: "action.700" }}
                  onClick={handleClear}
                  data-testid="chat-clear-button"
                >
                  <TrashIcon />
                </IconButton>
                <IconButton
                  aria-label="Close chat"
                  size="sm"
                  variant="ghost"
                  color="base.200"
                  _hover={{ bg: "action.700" }}
                  onClick={handleToggle}
                  data-testid="chat-close-button"
                >
                  <NavigationIcons.Cross size="md" />
                </IconButton>
              </Flex>
            </Box>

            {/* Chat body */}
            <Box css={chatBodyStyles}>
              {isLoading ? (
                <Flex
                  flex={1}
                  align="center"
                  justify="center"
                  direction="column"
                  gap={3}
                  p={6}
                >
                  <Spinner size="lg" color="action.600" />
                  <Text fontSize="sm" color="base.500">
                    Loading chat history...
                  </Text>
                </Flex>
              ) : (
                <MainContainer>
                  <ChatContainer>
                    <MessageList
                      typingIndicator={
                        isSending ? (
                          <TypingIndicator content="Thinking..." />
                        ) : undefined
                      }
                    >
                      {/* Welcome message if no history */}
                      {messages.length === 0 && (
                        <Message
                          model={{
                            message: `Hey ${firstName}! I'm your study assistant. Ask me anything about your lesson and I'll do my best to help.`,
                            sender: "assistant",
                            direction: "incoming",
                            position: "single"
                          }}
                        />
                      )}

                      {/* Chat messages */}
                      {messages.map(msg => (
                        <Message
                          key={msg.id}
                          model={{
                            message: msg.content,
                            sender: msg.role,
                            direction:
                              msg.role === "user" ? "outgoing" : "incoming",
                            position: "single"
                          }}
                        />
                      ))}
                    </MessageList>

                    <MessageInput
                      placeholder="Type your question..."
                      onSend={handleSend}
                      attachButton={false}
                      disabled={isSending}
                      data-testid="chat-message-input"
                    />
                  </ChatContainer>
                </MainContainer>
              )}

              {/* Error display */}
              {error && (
                <Box
                  px={4}
                  py={2}
                  bg="red.50"
                  borderTop="1px solid"
                  borderColor="red.200"
                >
                  <Text fontSize="xs" color="red.600">
                    {error}
                  </Text>
                </Box>
              )}
            </Box>
          </Box>
        </Portal>
      )}
    </>
  );
};

/** Simple chat bubble SVG icon */
const ChatIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H5.17L4 17.17V4H20V16Z"
      fill="currentColor"
    />
    <path d="M7 9H17V11H7V9Z" fill="currentColor" />
    <path d="M7 5H17V7H7V5Z" fill="currentColor" />
    <path d="M7 13H14V15H7V13Z" fill="currentColor" />
  </svg>
);

/** Simple trash can SVG icon */
const TrashIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19ZM8 9H16V19H8V9ZM15.5 4L14.5 3H9.5L8.5 4H5V6H19V4H15.5Z"
      fill="currentColor"
    />
  </svg>
);

export default ChatWidget;
